/**
 * TraceWritingPage
 * ----------------
 * Audio-first, finger-trace writing lessons. Rotates through letters A-Z and
 * numbers 0-9. The child sees stroke-order animation and traces on top.
 */
import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TraceLesson from '@/components/TraceLesson';

// A small, hand-written subset of stroke-order SVG paths. Each entry is one or
// more SVG "d" strings describing the correct stroke order inside a 320x320
// viewBox. New characters can be appended later without touching the component.
// Paths were sized so the character fills ~75% of the canvas.
const STROKE_PATHS = {
  // ===== Letters (uppercase) =====
  A: ['M 70 260 L 160 60 L 250 260', 'M 100 190 L 220 190'],
  B: ['M 100 60 L 100 260', 'M 100 60 Q 220 60 220 130 Q 220 160 160 160', 'M 100 160 Q 240 160 240 210 Q 240 260 100 260'],
  C: ['M 240 90 Q 160 50 100 110 Q 60 170 100 230 Q 160 280 240 240'],
  D: ['M 100 60 L 100 260', 'M 100 60 Q 240 60 240 160 Q 240 260 100 260'],
  E: ['M 230 60 L 100 60 L 100 260 L 230 260', 'M 100 160 L 210 160'],
  F: ['M 230 60 L 100 60 L 100 260', 'M 100 160 L 210 160'],
  G: ['M 240 90 Q 160 50 100 110 Q 60 170 100 230 Q 160 280 240 240 L 240 180 L 180 180'],
  H: ['M 100 60 L 100 260', 'M 230 60 L 230 260', 'M 100 160 L 230 160'],
  I: ['M 90 60 L 230 60', 'M 160 60 L 160 260', 'M 90 260 L 230 260'],
  J: ['M 90 60 L 230 60', 'M 190 60 L 190 220 Q 190 280 130 260 Q 90 240 90 210'],
  K: ['M 100 60 L 100 260', 'M 240 60 L 100 160 L 240 260'],
  L: ['M 100 60 L 100 260 L 240 260'],
  M: ['M 70 260 L 70 60 L 160 180 L 250 60 L 250 260'],
  N: ['M 70 260 L 70 60 L 250 260 L 250 60'],
  O: ['M 160 60 Q 60 60 60 160 Q 60 260 160 260 Q 260 260 260 160 Q 260 60 160 60'],
  P: ['M 100 260 L 100 60 Q 240 60 240 130 Q 240 180 100 180'],
  Q: ['M 160 60 Q 60 60 60 160 Q 60 260 160 260 Q 260 260 260 160 Q 260 60 160 60', 'M 190 200 L 260 270'],
  R: ['M 100 260 L 100 60 Q 240 60 240 130 Q 240 180 100 180', 'M 150 180 L 240 260'],
  S: ['M 240 90 Q 100 60 100 140 Q 100 190 220 180 Q 240 220 220 240 Q 160 280 70 240'],
  T: ['M 70 60 L 250 60', 'M 160 60 L 160 260'],
  U: ['M 70 60 L 70 200 Q 70 260 160 260 Q 250 260 250 200 L 250 60'],
  V: ['M 70 60 L 160 260 L 250 60'],
  W: ['M 60 60 L 110 260 L 160 150 L 210 260 L 260 60'],
  X: ['M 70 60 L 250 260', 'M 250 60 L 70 260'],
  Y: ['M 70 60 L 160 160 L 250 60', 'M 160 160 L 160 260'],
  Z: ['M 70 60 L 250 60 L 70 260 L 250 260'],
  // ===== Numbers =====
  '0': ['M 160 60 Q 70 60 70 160 Q 70 260 160 260 Q 250 260 250 160 Q 250 60 160 60'],
  '1': ['M 100 110 L 160 60 L 160 260', 'M 100 260 L 220 260'],
  '2': ['M 70 110 Q 100 50 160 60 Q 230 70 230 130 Q 230 180 70 260 L 240 260'],
  '3': ['M 70 90 Q 160 40 220 90 Q 240 140 160 160 Q 240 180 220 240 Q 160 290 70 240'],
  '4': ['M 220 260 L 220 60', 'M 220 60 L 60 200 L 240 200'],
  '5': ['M 230 60 L 90 60 L 80 160 Q 160 140 220 180 Q 250 240 180 260 Q 100 270 70 230'],
  '6': ['M 220 80 Q 120 60 80 180 Q 60 260 160 260 Q 240 260 240 200 Q 240 150 160 150 Q 100 150 80 200'],
  '7': ['M 70 60 L 240 60 L 110 260'],
  '8': ['M 160 160 Q 70 150 70 100 Q 70 50 160 60 Q 250 50 250 100 Q 250 150 160 160 Q 60 170 60 220 Q 60 275 160 260 Q 260 270 260 220 Q 260 170 160 160'],
  '9': ['M 240 180 Q 240 60 160 60 Q 70 60 70 130 Q 70 190 160 190 Q 220 190 240 150', 'M 240 150 L 240 260'],
};

const SEQUENCE = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z','0','1','2','3','4','5','6','7','8','9'];

const narrationFor = (char) => {
  if (/[0-9]/.test(char)) return `Let's practice writing the number ${char}. Start at the top and follow the dashes.`;
  return `Let's practice writing the letter ${char}. Start at the top and follow the dashes.`;
};

const TraceWritingPage = () => {
  const { childId } = useParams();
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(new Set());

  const char = SEQUENCE[idx];
  const strokes = useMemo(() => STROKE_PATHS[char] || null, [char]);

  const next = () => {
    setDone((prev) => new Set([...prev, char]));
    if (idx + 1 < SEQUENCE.length) setIdx(idx + 1);
  };
  const prev = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  if (done.size === SEQUENCE.length) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center bg-white p-8 rounded-2xl shadow-sm border border-slate-200">
          <Trophy className="w-16 h-16 mx-auto text-amber-500 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">You traced every letter and number!</h1>
          <Link to={childId ? `/learn/${childId}` : '/dashboard'}>
            <Button className="mt-4" data-testid="trace-finish-link">Back to Learning Hub</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link to={childId ? `/learn/${childId}` : '/dashboard'}>
            <Button variant="outline" size="sm" data-testid="trace-back-link">
              <ArrowLeft className="w-4 h-4 mr-2" /> Learning Hub
            </Button>
          </Link>
          <span className="text-sm text-slate-500">
            {idx + 1} / {SEQUENCE.length}
          </span>
        </div>

        {strokes ? (
          <TraceLesson
            key={char}
            char={char}
            strokePath={strokes}
            narration={narrationFor(char)}
            onDone={next}
          />
        ) : (
          <div className="text-center text-slate-500 p-10 bg-white rounded-2xl border border-slate-200">
            No stroke data for "{char}" yet. 
            <div className="mt-4">
              <Button onClick={next} data-testid="trace-skip-btn">Skip</Button>
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <Button variant="ghost" onClick={prev} disabled={idx === 0} data-testid="trace-prev-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>
          <Button variant="ghost" onClick={next} data-testid="trace-next-btn">
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TraceWritingPage;
